/**
 * DebugOverlay - Renders debug visualizations for the avatar mesh
 * Includes wireframe, normals, morph deltas, and skeleton helpers
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import * as THREE from 'three';

export interface DebugOverlayConfig {
  showNormals?: boolean;
  showMorphDeltas?: boolean;
  showBones?: boolean;
  showWireframe?: boolean;
  hideModel?: boolean; // Hide the avatar mesh when debugging
  sampleStep?: number;
  vectorScale?: number;
  morphTargetName?: string;
}

interface DebugOverlayProps {
  config: DebugOverlayConfig;
  onMeshInfo?: (info: { name: string; morphTargetCount: number } | null) => void;
  onMorphTargets?: (targets: string[]) => void;
}

const disposeObject = (object: THREE.Object3D) => {
  object.traverse((child: THREE.Object3D) => {
    const mesh = child as THREE.Mesh;
    if (mesh.geometry) {
      mesh.geometry.dispose();
    }
    if (mesh.material) {
      if (Array.isArray(mesh.material)) {
        mesh.material.forEach((mat) => mat.dispose());
      } else {
        mesh.material.dispose();
      }
    }
  });
};

export function DebugOverlay({ config, onMeshInfo, onMorphTargets }: DebugOverlayProps) {
  const groupRef = useRef<THREE.Group>(new THREE.Group());
  const { scene } = useThree();
  const [meshReady, setMeshReady] = useState(false);
  const frameCount = useRef(0);

  // Debug: Log when component mounts
  useEffect(() => {
    console.log('[DebugOverlay] Component mounted with config:', config);
    return () => {
      console.log('[DebugOverlay] Component unmounted');
    };
  }, []);

  const {
    showNormals = false,
    showMorphDeltas = false,
    showBones = false,
    showWireframe = false,
    hideModel = false,
    sampleStep = 80,
    vectorScale = 0.12,
    morphTargetName = ''
  } = config;

  // Store original visibility to restore later
  const originalVisibilityRef = useRef<Map<string, boolean>>(new Map());

  // Poll for mesh availability (only runs until mesh is found)
  useFrame(() => {
    if (meshReady) return;
    frameCount.current++;
    if (frameCount.current % 60 !== 0) return; // Check every 60 frames (less CPU)

    let found = false;
    scene.traverse((node) => {
      if (found) return; // Stop early
      const mesh = node as THREE.Mesh;
      if (mesh.isMesh && mesh.morphTargetDictionary && Object.keys(mesh.morphTargetDictionary).length > 0) {
        console.log('[DebugOverlay] Found mesh with morphs:', mesh.name, mesh.type);
        found = true;
      }
    });

    if (found) {
      console.log('[DebugOverlay] Mesh found, enabling overlay');
      setMeshReady(true);
    }
  });

  const rebuildOverlay = useCallback(() => {
    const group = groupRef.current;
    if (!group) return;

    // Remove group from its parent (if any) and clear children
    if (group.parent) {
      group.parent.remove(group);
    }
    while (group.children.length > 0) {
      const child = group.children[0];
      disposeObject(child);
      group.remove(child);
    }
    // Reset transform
    group.position.set(0, 0, 0);
    group.rotation.set(0, 0, 0);
    group.scale.set(1, 1, 1);
    group.matrix.identity();
    group.matrixWorld.identity();

    // Find any mesh with morph targets
    let foundMesh: THREE.Mesh | THREE.SkinnedMesh | null = null;
    scene.traverse((node) => {
      if (!foundMesh) {
        const mesh = node as THREE.Mesh;
        if (mesh.isMesh && mesh.morphTargetDictionary && Object.keys(mesh.morphTargetDictionary).length > 0) {
          foundMesh = mesh;
        }
      }
    });

    if (!foundMesh) {
      console.log('[DebugOverlay] No mesh with morphs found');
      onMeshInfo?.(null);
      return;
    }

    // Re-assign to help TypeScript understand this is not null after the check above
    const targetMesh = foundMesh as THREE.SkinnedMesh;
    const geometry = targetMesh.geometry;

    if (!geometry) {
      onMeshInfo?.(null);
      return;
    }

    console.log('[DebugOverlay] Building overlays for:', targetMesh.name);

    const positionAttr = geometry.attributes.position as THREE.BufferAttribute | undefined;
    const normalAttr = geometry.attributes.normal as THREE.BufferAttribute | undefined;

    const morphDict = targetMesh.morphTargetDictionary || {};
    const morphNames = Object.keys(morphDict).sort();
    onMorphTargets?.(morphNames);
    onMeshInfo?.({
      name: targetMesh.name || 'SkinnedMesh',
      morphTargetCount: morphNames.length
    });

    const step = Math.max(1, Math.floor(sampleStep));
    const vectorLength = Math.max(0.001, vectorScale);

    // Normals
    if (showNormals && positionAttr && normalAttr) {
      const linePositions: number[] = [];
      for (let i = 0; i < positionAttr.count; i += step) {
        const x = positionAttr.getX(i);
        const y = positionAttr.getY(i);
        const z = positionAttr.getZ(i);
        const nx = normalAttr.getX(i);
        const ny = normalAttr.getY(i);
        const nz = normalAttr.getZ(i);
        linePositions.push(x, y, z, x + nx * vectorLength, y + ny * vectorLength, z + nz * vectorLength);
      }
      const linesGeometry = new THREE.BufferGeometry();
      linesGeometry.setAttribute('position', new THREE.Float32BufferAttribute(linePositions, 3));
      const linesMaterial = new THREE.LineBasicMaterial({
        color: 0x66ccff,
        transparent: true,
        opacity: 0.85,
        depthTest: false,
        depthWrite: false
      });
      const normalsLines = new THREE.LineSegments(linesGeometry, linesMaterial);
      normalsLines.name = 'DebugNormals';
      normalsLines.renderOrder = 999;
      group.add(normalsLines);
      console.log('[DebugOverlay] Added normals:', linePositions.length / 6, 'lines');
    }

    // Morph deltas
    if (showMorphDeltas && positionAttr && geometry.morphAttributes.position && morphTargetName) {
      const morphIndex = morphDict[morphTargetName];
      if (morphIndex !== undefined) {
        const morphAttr = geometry.morphAttributes.position[morphIndex] as THREE.BufferAttribute | undefined;
        if (morphAttr) {
          const linePositions: number[] = [];
          for (let i = 0; i < positionAttr.count; i += step) {
            const x = positionAttr.getX(i);
            const y = positionAttr.getY(i);
            const z = positionAttr.getZ(i);
            const dx = morphAttr.getX(i);
            const dy = morphAttr.getY(i);
            const dz = morphAttr.getZ(i);
            // Only show if there's actual movement
            if (Math.abs(dx) > 0.0001 || Math.abs(dy) > 0.0001 || Math.abs(dz) > 0.0001) {
              linePositions.push(x, y, z, x + dx * vectorLength, y + dy * vectorLength, z + dz * vectorLength);
            }
          }
          const linesGeometry = new THREE.BufferGeometry();
          linesGeometry.setAttribute('position', new THREE.Float32BufferAttribute(linePositions, 3));
          const linesMaterial = new THREE.LineBasicMaterial({
            color: 0xffb347,
            transparent: true,
            opacity: 0.9,
            depthTest: false,
            depthWrite: false
          });
          const morphLines = new THREE.LineSegments(linesGeometry, linesMaterial);
          morphLines.name = 'DebugMorphDeltas';
          morphLines.renderOrder = 1000;
          group.add(morphLines);
          console.log('[DebugOverlay] Added morph deltas:', linePositions.length / 6, 'lines for', morphTargetName);
        }
      } else {
        console.log('[DebugOverlay] Morph target not found:', morphTargetName);
      }
    }

    // Wireframe - use skinned positions if available for proper alignment
    if (showWireframe && positionAttr) {
      let wireGeometry: THREE.WireframeGeometry | THREE.BufferGeometry;

      const skinnedMesh = targetMesh as THREE.SkinnedMesh;
      if (skinnedMesh.isSkinnedMesh && skinnedMesh.skeleton) {
        // Compute skinned vertex positions for accurate wireframe
        console.log('[DebugOverlay] Computing skinned positions for wireframe...');

        // Create a copy of the geometry with skinned positions
        const skinnedGeometry = geometry.clone();
        const skinnedPositions = new Float32Array(positionAttr.count * 3);

        // Get skinning data
        const skinIndex = geometry.attributes.skinIndex as THREE.BufferAttribute;
        const skinWeight = geometry.attributes.skinWeight as THREE.BufferAttribute;

        if (skinIndex && skinWeight) {
          // Update skeleton matrices
          skinnedMesh.skeleton.update();

          // Get bone matrices - need to account for bindMatrix
          const boneMatrices: THREE.Matrix4[] = [];
          const bindMatrixInverse = skinnedMesh.bindMatrixInverse.clone();
          const bindMatrix = skinnedMesh.bindMatrix.clone();

          for (let b = 0; b < skinnedMesh.skeleton.bones.length; b++) {
            const boneMatrix = new THREE.Matrix4();
            boneMatrix.multiplyMatrices(skinnedMesh.skeleton.bones[b].matrixWorld, skinnedMesh.skeleton.boneInverses[b]);
            boneMatrix.premultiply(bindMatrixInverse);
            boneMatrices.push(boneMatrix);
          }

          // Transform each vertex by weighted bone matrices
          const vertex = new THREE.Vector3();
          const skinned = new THREE.Vector3();
          const skinVertex = new THREE.Vector4();

          for (let i = 0; i < positionAttr.count; i++) {
            vertex.fromBufferAttribute(positionAttr, i);
            skinned.set(0, 0, 0);

            // Apply bindMatrix to get into bone space
            vertex.applyMatrix4(bindMatrix);

            const si = [
              skinIndex.getX(i),
              skinIndex.getY(i),
              skinIndex.getZ(i),
              skinIndex.getW(i)
            ];
            const sw = [
              skinWeight.getX(i),
              skinWeight.getY(i),
              skinWeight.getZ(i),
              skinWeight.getW(i)
            ];

            for (let j = 0; j < 4; j++) {
              const weight = sw[j];
              if (weight > 0) {
                const boneIdx = si[j];
                if (boneIdx < boneMatrices.length) {
                  skinVertex.set(vertex.x, vertex.y, vertex.z, 1);
                  skinVertex.applyMatrix4(boneMatrices[boneIdx]);
                  skinned.x += skinVertex.x * weight;
                  skinned.y += skinVertex.y * weight;
                  skinned.z += skinVertex.z * weight;
                }
              }
            }

            skinnedPositions[i * 3] = skinned.x;
            skinnedPositions[i * 3 + 1] = skinned.y;
            skinnedPositions[i * 3 + 2] = skinned.z;
          }

          skinnedGeometry.setAttribute('position', new THREE.BufferAttribute(skinnedPositions, 3));
          wireGeometry = new THREE.WireframeGeometry(skinnedGeometry);
          console.log('[DebugOverlay] Created skinned wireframe');
        } else {
          // No skinning data, fall back to static wireframe
          wireGeometry = new THREE.WireframeGeometry(geometry);
          console.log('[DebugOverlay] No skinning data, using static wireframe');
        }
      } else {
        wireGeometry = new THREE.WireframeGeometry(geometry);
      }

      const wireMaterial = new THREE.LineBasicMaterial({
        color: 0x888888,
        opacity: 0.4,
        transparent: true,
        depthTest: false
      });
      const wireframe = new THREE.LineSegments(wireGeometry, wireMaterial);
      wireframe.name = 'DebugWireframe';
      wireframe.renderOrder = 998;
      // For skinned wireframe, add directly to scene (positions are in world space)
      if ((targetMesh as THREE.SkinnedMesh).isSkinnedMesh) {
        scene.add(wireframe);
        console.log('[DebugOverlay] Added skinned wireframe to scene');
      } else {
        group.add(wireframe);
        console.log('[DebugOverlay] Added wireframe to group');
      }
    }

    // Bones/Skeleton (only for SkinnedMesh)
    if (showBones) {
      const skinnedMesh = targetMesh as THREE.SkinnedMesh;
      if (skinnedMesh.skeleton) {
        const skeletonHelper = new THREE.SkeletonHelper(skinnedMesh);
        const helperMaterial = skeletonHelper.material as THREE.LineBasicMaterial;
        helperMaterial.color.setHex(0x9b59ff);
        helperMaterial.transparent = true;
        helperMaterial.opacity = 0.9;
        helperMaterial.depthTest = false;
        skeletonHelper.name = 'DebugSkeleton';
        skeletonHelper.renderOrder = 1001;
        scene.add(skeletonHelper);
        console.log('[DebugOverlay] Added skeleton helper');
      }
    }

    // Add the overlay group as a CHILD of the mesh so it inherits all transforms
    // This ensures wireframe/normals align exactly with the rendered mesh
    targetMesh.add(group);
    console.log('[DebugOverlay] Added overlay group as child of mesh');

    // Handle model visibility - hide mesh materials instead of visibility
    // to avoid hiding debug children
    if (hideModel) {
      scene.traverse((node) => {
        const mesh = node as THREE.Mesh;
        // Only hide actual model meshes, not debug helpers
        if (mesh.isMesh &&
          !mesh.name.startsWith('Debug') &&
          mesh.material) {
          if (!originalVisibilityRef.current.has(mesh.uuid)) {
            // Store original opacity
            const mat = mesh.material as THREE.Material;
            originalVisibilityRef.current.set(mesh.uuid, (mat as any).opacity ?? 1);
            // Make fully transparent
            (mat as any).transparent = true;
            (mat as any).opacity = 0;
            mat.needsUpdate = true;
          }
        }
      });
      console.log('[DebugOverlay] Model hidden via transparency');
    } else {
      // Restore original opacity
      scene.traverse((node) => {
        const mesh = node as THREE.Mesh;
        if (mesh.isMesh && mesh.material) {
          const originalOpacity = originalVisibilityRef.current.get(mesh.uuid);
          if (originalOpacity !== undefined) {
            const mat = mesh.material as THREE.Material;
            (mat as any).opacity = originalOpacity;
            mat.needsUpdate = true;
          }
        }
      });
      originalVisibilityRef.current.clear();
    }
  }, [scene, showNormals, showMorphDeltas, showBones, showWireframe, hideModel, sampleStep, vectorScale, morphTargetName, onMeshInfo, onMorphTargets]);

  useEffect(() => {
    if (!meshReady) return;
    rebuildOverlay();

    return () => {
      const group = groupRef.current;
      if (group) {
        // Remove from parent mesh
        if (group.parent) {
          group.parent.remove(group);
        }
        // Clear children
        while (group.children.length > 0) {
          const child = group.children[0];
          disposeObject(child);
          group.remove(child);
        }
      }
      // Also clean up skeleton helper from scene
      const skeleton = scene.getObjectByName('DebugSkeleton');
      if (skeleton) {
        scene.remove(skeleton);
        disposeObject(skeleton);
      }
      // Clean up skinned wireframe from scene (if added there)
      const wireframe = scene.getObjectByName('DebugWireframe');
      if (wireframe) {
        scene.remove(wireframe);
        disposeObject(wireframe);
      }
      // Restore model visibility
      scene.traverse((node) => {
        if (node instanceof THREE.Mesh) {
          const original = originalVisibilityRef.current.get(node.uuid);
          if (original !== undefined) {
            node.visible = original;
          }
        }
      });
      originalVisibilityRef.current.clear();
    };
  }, [meshReady, rebuildOverlay, scene]);

  // Note: Group is added as child of mesh in rebuildOverlay, not to scene directly
  // Cleanup happens in the rebuildOverlay cleanup function

  // This component doesn't render JSX - it manages a THREE.Group imperatively
  return null;
}

export default DebugOverlay;
